import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import ws from '../services/wsService';
import { playNotificationDing, isNotificationSoundMuted } from '../utils/notificationSound';
import { useAuthStore } from '../stores/authStore';

/**
 * Global "new ticket" notifier. Mounts once in <Layout/> and stays active
 * on every authenticated page.
 *
 * Behaviour:
 *   - WS `new_ticket` → short ding + clickable toast for ~6s
 *   - Clicking the toast navigates to the ticket
 *   - Dedup within 5s by ticketId so a reconnect-replay doesn't double-fire
 *   - Skips if the operator is already on /tickets/<id> for that ticket
 *     (they're literally looking at it — no point yelling)
 *   - Sound is muted via the bell-toggle in the header (persisted in localStorage)
 */
export function useGlobalTicketNotifications(): void {
  const navigate = useNavigate();
  const recentRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const onNewTicket = (payload: any) => {
      console.debug('[notif] new_ticket received', payload);
      const ticket = payload?.ticket || payload;
      const id: string | undefined = ticket?.id || ticket?.ticketId;
      if (!id) {
        console.debug('[notif] new_ticket ignored — no id in payload');
        return;
      }
      const me = useAuthStore.getState().user?.id;
      // «Липкость к клубу»: в этом чате уже работает коллега на смене — ему
      // звук, остальным только тост.
      const sticky = payload?.stickyOperatorId;
      const quiet = !!(sticky && me && String(sticky) !== String(me));

      // Dedup window — same WS event can arrive twice during reconnect.
      const now = Date.now();
      const lastAt = recentRef.current.get(id);
      if (lastAt && now - lastAt < 5000) return;
      recentRef.current.set(id, now);
      // Trim the map periodically so it doesn't grow forever.
      if (recentRef.current.size > 50) {
        const cutoff = now - 60_000;
        recentRef.current.forEach((t, k) => {
          if (t < cutoff) recentRef.current.delete(k);
        });
      }

      // Don't yell at the operator if they're already on this exact ticket.
      if (typeof window !== 'undefined'
          && window.location.pathname === `/tickets/${id}`) {
        return;
      }

      // Sound — best-effort. First call before any user gesture may be
      // silently dropped by the browser; that's acceptable.
      if (!quiet && !isNotificationSoundMuted()) {
        try { playNotificationDing(); } catch { /* ignore */ }
      }

      const number = ticket?.ticketNumber || 'без номера';
      const clientLabel = pickClientLabel(ticket);
      const subject = (ticket?.subject || '').slice(0, 60);

      // Hard-coded palette for the notification card: react-hot-toast renders
      // its own white container in light mode and a dark one in dark mode,
      // but `--foreground` doesn't always flip with it (e.g. when the toast
      // lives in a portal outside the theme provider) — that gave us
      // black-on-dark unreadable text. We paint the card ourselves with a
      // fixed dark slate background and explicit light text so it's legible
      // in either theme.
      toast.custom(
        (t) => (
          <div
            onClick={() => {
              toast.dismiss(t.id);
              navigate(`/tickets/${id}`);
            }}
            className={`cursor-pointer flex items-start gap-3 min-w-[280px] max-w-sm
                        rounded-lg border border-slate-700/60 bg-slate-900/95
                        shadow-xl backdrop-blur-sm px-4 py-3
                        ring-1 ring-emerald-400/20
                        ${t.visible ? 'animate-in fade-in slide-in-from-top-2' : 'opacity-0'}`}
            role="button"
          >
            <div className="text-2xl leading-none select-none">🎫</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white">
                Новая заявка #{number}
              </div>
              <div className="text-xs text-slate-300 truncate">
                {clientLabel}
              </div>
              {subject && (
                <div className="text-xs text-slate-200 truncate mt-0.5">
                  {subject}
                </div>
              )}
              <div className="text-[10px] text-emerald-400 mt-1 font-medium">
                Открыть →
              </div>
            </div>
          </div>
        ),
        { duration: 6000, position: 'top-right' }
      );
    };

    ws.joinDashboard();
    ws.on('new_ticket', onNewTicket);
    return () => {
      ws.off('new_ticket', onNewTicket);
    };
  }, [navigate]);
}

/**
 * "Whom from?" line for the toast — falls back through username → name →
 * group title → "клиент".
 */
function pickClientLabel(ticket: any): string {
  if (!ticket) return 'клиент';
  if (ticket.telegramChatType === 'private') {
    if (ticket.telegramUsername) return `@${ticket.telegramUsername}`;
    const name = `${ticket.telegramFirstName || ''} ${ticket.telegramLastName || ''}`.trim();
    return name || 'клиент';
  }
  return ticket.telegramChatTitle || 'группа';
}
